<template>
    <div class="appointments-unique-check">
        <template v-if="isLoading">
            <div class="appointments-unique-check__loading">
                <IconLoad class="appointments-unique-check__loader" />
                <div class="appointments-unique-check__loading-text">
                    Ищем клиента...
                </div>
            </div>
        </template>
        <template v-else>
            <template v-if="isClientUnique">
                <div class="appointments-unique-check__status">
                    <div class="appointments-unique-check__status-icon">
                        <CheckStatus class-name="icon-stroke icon_color_blue" />
                    </div>

                    <p class="appointments-unique-check__title">
                        Клиент уникален
                    </p>
                    <p class="appointments-unique-check__description">
                        Вы будете перенаправлены на следующий<br>
                        шаг записи в офис через 3 секунды...
                    </p>
                </div>
            </template>
            <template v-else-if="isClientNotUnique">
                <div class="appointments-unique-check__status">
                    <div class="appointments-unique-check__status-body">
                        <div class="appointments-unique-check__status-icon is-not-unique">
                            <CloseSmall class-name="icon-stroke icon_color_gray" />
                        </div>

                        <p class="appointments-unique-check__title">
                            Клиент не уникален
                        </p>
                        <p class="appointments-unique-check__description">
                            Этот клиент есть в базах данных ТЕКТА.<br>
                            Получить комиссию по сделке невозможно.
                        </p>
                    </div>
                </div>

                <div class="appointments-unique-check__actions">
                    <STBaseButton text="Отмена записи"
                                  modify="button_theme_white button_size_small"
                                  @click="cancel" />

                    <STBaseButton text="Попробовать снова"
                                  modify="button_size_small"
                                  @click="reset" />
                </div>
            </template>
            <template v-else-if="isError">
                <div class="appointments-unique-check__status">
                    <div class="appointments-unique-check__alert-icon">
                        <IconAlert />
                    </div>
                    <p class="appointments-unique-check__title">
                        Ой, что-то пошло не так
                    </p>
                    <p class="appointments-unique-check__description">
                        Произошла непредвиденная ошибка<br>Пожалуйста попробуйте позже.
                    </p>
                </div>

                <div class="appointments-unique-check__actions">
                    <STBaseButton text="Отмена записи"
                                  modify="button_theme_white button_size_small"
                                  @click="cancel" />

                    <STBaseButton text="Попробовать снова"
                                  modify="button_size_small"
                                  @click="reset" />
                </div>
            </template>
            <template v-else>
                <div class="appointments-unique-check__check-client">
                    <div class="appointments-unique-check__check-title">
                        Проверка клиента
                    </div>
                    <div class="appointments-unique-check__check-description">
                        Чтобы продолжить запись проверьте вашего клиента на уникальность
                    </div>
                    <div class="appointments-unique-check__check-description">
                        Уведомляем Вас о&nbsp;том, что фиксация клиента производится только <b>при личном визите совместно с&nbsp;клиентом</b> в&nbsp;офис продаж и&nbsp;подписании акта просмотра объекта
                    </div>

                    <form class="appointments-unique-check__form"
                          @submit.prevent="triggerGuard">
                        <div class="appointments-unique-check__inputs">
                            <STSelect
                                id="project-select"
                                v-model="selectedProjectId"
                                :options="projects"
                                :multiple="false"
                                name="project-select"
                                label="Выберите проект" />

                            <STInput
                                id="user-phone"
                                ref="phoneInput"
                                :value="userPhone"
                                :modify="{'is-error': $v.userPhone.$dirty && $v.userPhone.$invalid}"
                                :error="getErrorText($v.userPhone)"
                                name="user-phone"
                                label="Введите номер телефона клиента"
                                @input="onPhoneInput"
                                @focus="onPhoneFocus" />

                            <STInput
                                id="user-name"
                                v-model.trim="clientName"
                                :modify="{'is-error': $v.clientName.$dirty && $v.clientName.$invalid}"
                                :error="getErrorText($v.clientName)"
                                name="request-callback-user-name"
                                label="Введите имя клиента"
                                @input="$v.clientName.$touch()"
                                @focus="$v.clientName.$touch()" />

                            <STInput
                                id="user-surname"
                                v-model.trim="clientSurname"
                                :modify="{'is-error': $v.clientSurname.$dirty && $v.clientSurname.$invalid}"
                                :error="getErrorText($v.clientSurname)"
                                name="request-callback-user-name"
                                label="Введите фамилию клиента"
                                @input="$v.clientSurname.$touch()"
                                @focus="$v.clientSurname.$touch()" />
                        </div>

                        <div class="appointments-unique-check__submit">
                            <STBaseButton text="Проверить"
                                          icon-after="IconRightFill"
                                          icon-after-modify="icon-fill icon_color_white"
                                          modify="button_size_small"
                                          :disabled="!isFieldsFilled"
                                          @click="checkUnique" />
                        </div>
                    </form>
                </div>
            </template>
        </template>
    </div>
</template>

<script>
import CheckStatus from '@/components/Icons/CheckStatus';
import CloseSmall from '@/components/Icons/CloseSmall';
import IconAlert from '@/components/Icons/Alert';
import IconLoad from '@/components/Icons/LoadGrey';
import {mapActions, mapGetters} from 'vuex';
import {maxLength, minLength, required} from 'vuelidate/lib/validators';
import {
    handlePhoneFocus,
    handlePhoneInput,
    isPhoneComplete
} from '@/helpers/phone';

export default {
    name: 'AppointmentsUniqueCheck',

    components: {
        CheckStatus,
        CloseSmall,
        IconAlert,
        IconLoad
    },

    validations: {
        clientName: {
            required,
            letter(value) {
                return /^[A-Za-zА-Яа-яЁё]+$/.test(value);
            },
            minLength: minLength(3),
            maxLength: maxLength(40)
        },
        clientSurname: {
            required,
            letter(value) {
                return /^[A-Za-zА-Яа-яЁё]+$/.test(value);
            },
            minLength: minLength(3),
            maxLength: maxLength(40)
        },
        userPhone: {
            required,
            phoneLength: isPhoneComplete
        }
    },

    data() {
        return {
            isLoading        : false,
            isError          : false,
            isClientUnique   : false,
            isClientNotUnique: false,
            selectedProjectId: '',
            userPhone        : '',
            clientName       : '',
            clientSurname    : ''
        };
    },

    computed: {
        ...mapGetters({
            complexes: 'GET_DISTRICT_LIST'
        }),

        projects() {
            if (!this.complexes?.length) {
                return [];
            }

            return this.complexes.map((complex) => ({
                text : complex.name,
                value: complex.id
            }));
        },

        isFieldsFilled() {
            return this.selectedProjectId && isPhoneComplete(this.userPhone) && !this.$v.$invalid;
        },

        clearPhone() {
            return this.userPhone.replace(/\D+/g, '').replace(/^\d/, '7');
        }
    },

    methods: {
        ...mapActions(['checkUserUnique']),

        getErrorText(value) {
            switch (false) {
                case value.required:
                    return 'Это обязательное поле';
                case value.minLength:
                    return `Минимальная длина ${value.$params?.minLength?.min} символа`;
                case value.maxLength:
                    return `Максимальная длина длина ${value.$params?.maxLength?.max} символов`;
                case value.letter:
                    return 'Допускаются только символы алфавита';
                case value.phoneLength:
                    return 'Некорректный номер телефона';
            }
        },

        /**
         * Селект при открытии триггерит сабмит формы
         * Данный метод блокирует случайные встроенные сабмиты не по кнопке
         * @return {null}
         */
        triggerGuard() {
            return null;
        },

        onPhoneFocus() {
            handlePhoneFocus(this, 'userPhone', 'phoneInput');
            this.$v.userPhone.$touch();
        },

        onPhoneInput(value) {
            handlePhoneInput(this, 'userPhone', 'phoneInput', value);
            this.$v.userPhone.$touch();
        },

        /**
         * Отправляет запрос на проверку уникальности юзера
         */
        async checkUnique() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            this.isError = false;
            this.isClientUnique = false;
            this.isClientNotUnique = false;
            this.isLoading = true;

            const payload = {
                phone        : this.clearPhone,
                projectId    : this.selectedProjectId,
                clientName   : this.clientName,
                clientSurname: this.clientSurname
            };

            await this.checkUserUnique(payload)
                .then((response) => {
                    const {data} = response;

                    if (data.error) {
                        const code = data.notCounter ? 1 : 3;

                        this.setErrorState();
                        this.returnError({ code });

                        throw new Error(`В запросе произошла ошибка - ${data.error}`);
                    }

                    // messageCode
                    // 0 - Ошибка интеграции
                    // 1 - Клиент уникальный
                    // 2 - Клиент не уникальный
                    // 3 - Истек срок жизни AccessToken
                    // 4 - Некорректный AccessToken
                    const isShowError = !data.data || !+data.data.messageCode || +data.data.messageCode > 2;
                    const code = 1;

                    if (isShowError) {
                        this.setErrorState();
                        this.returnError({ code });

                        throw new Error(`Ошибка в проверки пользователя на уникальность`);
                    }

                    switch (+data.data.messageCode) {
                        case 1:
                            this.isLoading = false;
                            this.isClientUnique = true;
                            this.isClientNotUnique = false;
                            this.isError = false;
                            this.continueCreating(payload.projectId, payload.phone, payload.clientName, payload.clientSurname);

                            break;
                        case 2:
                            this.isLoading = false;
                            this.isClientUnique = false;
                            this.isClientNotUnique = true;
                            this.isError = false;

                            break;
                        default:
                            this.setErrorState();
                            this.returnError({ code });

                            throw new Error(`Ошибка при проверке на уникальность`);
                    }
                })
                .catch((error) => {
                    this.setErrorState();
                    this.returnError({
                        code: 5,
                        data: error.response
                    });

                    throw new Error(error);
                });
        },

        continueCreating(projectId, phone, clientName, clientSurname) {
            setTimeout(() => {
                this.$emit('continue', projectId, phone, clientName, clientSurname);
            }, 3000);
        },

        cancel() {
            this.$emit('cancel');
        },

        reset() {
            this.isLoading = false;
            this.isError = false;
            this.isClientUnique = false;
            this.isClientNotUnique = false;
            this.$v.$reset();
        },

        /**
         * Устанавливает флаги для ошибки при проверке на уникальность
         */
        setErrorState() {
            this.isError = true;
            this.isLoading = false;
            this.isClientUnique = false;
            this.isClientNotUnique = false;
        }
    }
};
</script>

<style lang="scss">
@import "AppointmentsUniqueCheck";
</style>
